// Klargo Design System — Transaction record (/affarshistorik/:id). The
// mockup's own separate interface for a clicked history row
// (Klargo-Reference/src/pages/TransactionRecord.tsx): a full page, not a
// side sheet — header (status, plate, title, price, parties), a dossier
// sheet (what was agreed + the retained documents), then a completion
// ledger. Data fetching (GetContractMainById, the PDF download, the
// resume/continue-deal flow) is unchanged from the previous side-sheet
// panel; only the presentation moved to its own route. Anything the mockup
// shows that this app has no backend field for (a business reference beyond
// the contract id, a second retained document, agreed keys/known
// defects/equipment — none of those are ever persisted past the live
// session) is left out rather than invented, matching the mockup's own rule
// that a fact without its own data never renders.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { Link, useNavigate, useParams } from "react-router-dom";
import ApiCall from "../../Apicall/ApiCall";
import { escapeHtml, showContractUnavailableDialog } from "../components/ds/dealDialogs";
import { announceDealCancelled } from "../lib/dealSession";
import { buyerResumeState } from "../../utils/postLoginRedirect";
import AuthShell from "../components/ds/AuthShell";
import { Button } from "../components/ui/button";
import ContractPreviewModal from "../components/ds/ContractPreviewModal";
import PaperPreviewModal from "../components/ds/PaperPreviewModal";
import { buildReceiptDocument } from "../lib/dealReceipt";
import { formatPersonName } from "../lib/formatName";
import { downloadContractPdfById, downloadEvidencePackage, downloadStoredContractPdf } from "../lib/contractPdf";
import RegistrationPlate from "../components/common/RegistrationPlate";
import { DownloadMark, ForwardArrow, PdfFileMark } from "../components/ds/StatusMark";
import { formatDateLocalized, formatHistoricMileage, formatRegNumber, formatSekAmount } from "../lib/format";
import { useLanguage } from "../components/layout/LanguageSwitcher";

const mapVehicleType = (raw) => {
  if (!raw) return "other";
  const s = raw.toLowerCase();
  if (s === "car" || s === "bil") return "car";
  if (s === "mc" || s === "motorcycle") return "mc";
  if (s === "moped") return "moped";
  if (s === "husbil" || s === "camper" || s === "rv") return "camper";
  if (s === "lätt lastbil" || s === "light truck" || s === "lighttruck" || s === "light_truck") return "light_truck";
  return "other";
};

/** One register cell — label, primary value, secondary context. Matches the
 * mockup's AgreementRegister/RegisterCell: open tracks from 560px, hairline
 * stack below it. */
const RegisterCell = ({ label, value, secondary }) => (
  <div className="border-[rgba(20,36,46,.12)] py-3 max-[559px]:first:pt-0 max-[559px]:last:pb-0 [&:not(:first-child)]:border-t min-[560px]:pr-12 min-[560px]:py-0.5 min-[560px]:last:pr-0 min-[560px]:[&:not(:first-child)]:border-t-0">
    <div className="ds-label">{label}</div>
    <div className="mt-1 text-[17px] font-semibold leading-tight tabular-nums text-ds-ink">{value}</div>
    {secondary && <div className="mt-[3px] text-[13px] text-ds-muted">{secondary}</div>}
  </div>
);

export default function TransactionRecordPage() {
  const { t } = useTranslation();
  const { t: tp, languageCode } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams();

  const [detail, setDetail] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [contractPreviewOpen, setContractPreviewOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  // The deal log (tbl_DealLog): the recorded steps of this deal, oldest first.
  const [dealLogs, setDealLogs] = useState([]);
  // The steps of buyers the seller disconnected as the wrong buyer — kept out of the ledger above
  // and shown only on request. Empty for anyone but the seller, so the link is theirs alone.
  const [wrongBuyerLogs, setWrongBuyerLogs] = useState([]);
  const [wrongBuyerOpen, setWrongBuyerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const sessionRes = await ApiCall({ url: "/Session/GetCurrentLoginInformations", method: "GET" });
        const uid = sessionRes?.data?.result?.user?.id;
        if (active) setCurrentUserId(uid);

        const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: id } });
        const data = res?.data?.result || null;
        if (!active) return;
        if (!data || (data.sellerUserId !== uid && data.buyerUserId !== uid)) {
          setNotFound(true);
        } else {
          setDetail(data);
        }
      } catch (err) {
        console.error("Failed to load transaction record", err);
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [res, wrongRes] = await Promise.all([
          ApiCall({ url: "/DealLog/GetByContract", method: "GET", params: { Id: id } }),
          ApiCall({ url: "/DealLog/GetWrongBuyerLog", method: "GET", params: { Id: id } }),
        ]);
        const rows = res?.data?.result || res?.result || [];
        const wrongRows = wrongRes?.data?.result || wrongRes?.result || [];
        if (active) setDealLogs(Array.isArray(rows) ? rows : []);
        if (active) setWrongBuyerLogs(Array.isArray(wrongRows) ? wrongRows : []);
      } catch (err) {
        console.error("Failed to load the deal log", err);
        if (active) setDealLogs([]);
        if (active) setWrongBuyerLogs([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const localDate = (d) => (d ? formatDateLocalized(d, languageCode) : "");
  const localTime = (d) =>
    new Date(d).toLocaleTimeString(languageCode === "en" ? "en-GB" : "sv-SE", { hour: "2-digit", minute: "2-digit" });
  // One sentence per recorded step; the actor's name, the registration number and the price come
  // from the log row itself.
  const logSentence = (row) => {
    const keyed = ["price_confirmed", "bankid_started", "bankid_verified", "bankid_signed", "deal_cancelled"].includes(row.eventType);
    const key = `ds.record.log.${row.eventType}${keyed ? `_${row.actorRole === "seller" ? "seller" : "buyer"}` : ""}`;
    const template = tp(key);
    if (template === key) return null;
    const price = row.eventType === "price_confirmed" && row.details ? formatSekAmount(Number(row.details), languageCode) : "";
    return template
      .replace("{name}", row.actorName || "")
      .replace("{reg}", row.eventType === "car_searched" ? row.details || "" : "")
      .replace("{price}", price)
      .replace(/s{2,}/g, " ")
      .trim();
  };

  // One ledger row, shared by the deal log and the wrong-buyer log below it, so the two read
  // identically — same sentence, same timestamp, same rule beneath.
  const logRow = (row, testId) => {
    const sentence = logSentence(row);
    if (!sentence) return null;
    return (
      <li
        key={row.id}
        data-testid={testId}
        className="border-b border-[rgba(20,36,46,.12)] py-2.5 min-[800px]:flex min-[800px]:items-baseline min-[800px]:justify-between min-[800px]:gap-x-8"
      >
        <span className="block text-[15px] font-medium leading-[1.5] text-ds-ink">{sentence}</span>
        <time dateTime={row.creationTime} className="mt-[2px] block text-[13.5px] tabular-nums text-ds-muted min-[800px]:mt-0 min-[800px]:flex-none">
          {localDate(row.creationTime)} · {localTime(row.creationTime)}
        </time>
      </li>
    );
  };

  // The deal's state, derived once here so the view-contract gate below and the header
  // further down can never disagree about whether this deal was completed.
  const dealStatus = detail?.dealState || (detail?.dealComplete ? "completed" : "in_progress");

  // "View the contract" opens the exact same contract document the buyer and
  // seller themselves see (ContractDocumentView, shared with the standalone
  // /Contract/:id page) in a modal ABOVE this page — not a navigation to a
  // separate route — matching the mockup's own document-preview behavior.
  // Only a completed deal has an agreement to open: a deal that was cancelled, ran out of
  // time, or is still being signed produced no contract, so say that instead of opening a
  // document that would misrepresent an unsigned draft as the concluded agreement.
  const handleViewContract = () => {
    if (dealStatus !== "completed") {
      showContractUnavailableDialog(tp, dealStatus);
      return;
    }
    setContractPreviewOpen(true);
  };

  // The deal receipt is its own document - the paper the seller's closing page opens, the same for
  // both parties - and is held to the same rule as the contract: only a completed deal has one.
  const handleViewReceipt = () => {
    if (dealStatus !== "completed") {
      showContractUnavailableDialog(tp, dealStatus);
      return;
    }
    setReceiptOpen(true);
  };

  // The shelf's own download icon button — the same real contract PDF
  // ContractPdf/Download already generates (unchanged endpoint), for both
  // hardcoded shelf rows since this backend has no separate file of its own
  // for either of them.
  const handleDownloadPdf = async () => {
    try {
      // Once anyone has signed, the agreement is the document Klargo preserved before
      // signing (or, fully signed, its stored signed copy) — never re-rendered from
      // current data. Unsigned drafts still render from the live contract view.
      if (detail?.dealComplete || detail?.sellerSignedAt || detail?.buyerSignedAt) {
        await downloadStoredContractPdf({ id, lang: languageCode });
        return;
      }
      await downloadContractPdfById({
        t,
        id,
        fileName: `Klargo-Contract-${detail?.carInfoRegistrationNo || id}.pdf`,
      });
    } catch (err) {
      console.error("PDF download failed", err);
    }
  };

  const handleDownloadEvidence = async () => {
    try {
      await downloadEvidencePackage(id);
    } catch (err) {
      console.error("Evidence download failed", err);
    }
  };

  // The seller cancels an in-progress sale from here: the same server-side cancel as on the QR step
  // (seller only, logged in the deal log). A connected buyer is notified over SignalR.
  const handleCancelSale = async () => {
    const result = await Swal.fire({
      html: `
        <div class="ds-swal-price ds-swal-price-confirm">
          <p class="ds-swal-price-title">${escapeHtml(t("seller.confirmCancel.title"))}</p>
          <p class="ds-swal-price-desc">${escapeHtml(t("seller.confirmCancel.body"))}</p>
          <div class="ds-swal-price-actions">
            <button type="button" class="ds-swal-price-cancel" id="ds-swal-tr-keep">${escapeHtml(t("seller.confirmCancel.cancel"))}</button>
            <button type="button" class="ds-swal-price-disconnect" id="ds-swal-tr-cancel">${escapeHtml(t("seller.confirmCancel.confirm"))}</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      customClass: { popup: "ds-swal-popup", htmlContainer: "ds-swal-price-container" },
      didOpen: (popup) => {
        popup.querySelector("#ds-swal-tr-keep").addEventListener("click", () => Swal.close());
        popup.querySelector("#ds-swal-tr-cancel").addEventListener("click", () => Swal.close({ isConfirmed: true }));
      },
    });
    if (!result.isConfirmed) return;
    try {
      await ApiCall({ url: "/ContractMain/DealCanceledBySeller", method: "POST", data: { id: detail.id } });
      try {
        if (String(localStorage.getItem("currentContractID")) === String(detail.id)) localStorage.removeItem("currentContractID");
      } catch (_) {}
      await announceDealCancelled(detail.id);
      const [res, logRes] = await Promise.all([
        ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: id } }),
        ApiCall({ url: "/DealLog/GetByContract", method: "GET", params: { Id: id } }),
      ]);
      if (res?.data?.result) setDetail(res.data.result);
      const rows = logRes?.data?.result || logRes?.result || [];
      setDealLogs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Failed to cancel sale" });
    }
  };

  // Resume an in-progress deal where the user left off — unchanged from the
  // side-sheet panel this page replaces.
  const handleContinue = () => {
    if (!detail) return;
    localStorage.setItem("currentContractID", detail.id);
    if (detail.sellerUserId === currentUserId) {
      navigate("/SellerProfile");
    } else {
      localStorage.setItem("data", JSON.stringify({ contractId: detail.id, sellerUserId: detail.sellerUserId }));
      if (detail.buyerDealConnected) {
        navigate("/BuyerProfile", { state: buyerResumeState(detail) });
      } else {
        navigate("/BuyerSuccess");
      }
    }
  };

  if (loading) {
    return (
      <AuthShell t={t} activeKey="history">
        <section className="ds-animate-page ds-auth-rail max-w-[1040px] pb-[var(--ds-tr-bottom)] pt-[var(--ds-record-top)]">
          <div className="flex justify-center py-16" role="status" aria-label={t("history.title")}>
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
              style={{ borderTopColor: "var(--ds-green)" }}
            />
          </div>
        </section>
      </AuthShell>
    );
  }

  if (notFound || !detail) {
    return (
      <AuthShell t={t} activeKey="history">
        <section className="ds-animate-page ds-auth-rail max-w-[1040px] pb-[var(--ds-tr-bottom)] pt-[var(--ds-record-top)]">
          <div className="mx-auto w-full max-w-[640px] px-[clamp(18px,4vw,40px)] py-16">
            <div className="rounded-[14px] border border-ds-sheet-edge bg-ds-sheet p-[clamp(28px,4vw,44px)]">
              <h1 className="ds-title-chapter m-0 mb-2.5">{tp("ds.record.notFound.title")}</h1>
              <p className="ds-body m-0 mb-6">{tp("ds.record.notFound.body")}</p>
              <button type="button" className="ds-action-ink" onClick={() => navigate("/affarshistorik")}>
                {tp("ds.record.back")}
              </button>
            </div>
          </div>
        </section>
      </AuthShell>
    );
  }

  const isSeller = detail.sellerUserId === currentUserId;
  const isBuyer = !isSeller;
  const vehicleTypeKey = mapVehicleType(detail.vahicleTypeOptionVahicleTypeName);
  const vehicleName =
    [detail.carInfoTradeName || detail.carInfoVehicleDesignation || "", detail.carInfoYearModel || ""]
      .join(" ")
      .trim() || (detail.carInfoRegistrationNo ? formatRegNumber(detail.carInfoRegistrationNo) : "");
  const title = tp(isBuyer ? "ds.record.title.purchase" : "ds.record.title.sale").replace("{vehicle}", vehicleName);
  const crumbLeaf = detail.carInfoRegistrationNo ? formatRegNumber(detail.carInfoRegistrationNo) : "";

  const status = dealStatus;
  const STATUS_LABELS = {
    completed: t("history.status.completed"),
    in_progress: t("history.status.inProgress"),
    expired: t("history.status.expired"),
    cancelled: t("history.status.cancelled"),
  };
  // When the sale was completed: the moment the seller stated the change of ownership reported -
  // the last stage of a sale, recorded in the deal's log, and the date the closing page states - or,
  // for a deal with no such statement, when it was signed.
  const ownershipReportedAt = dealLogs.find((row) => row.eventType === "ownership_reported")?.creationTime || null;
  const completedDate = ownershipReportedAt || detail.buyerSignedAt || detail.sellerSignedAt || null;
  // The header's date: when it was completed, when it expired (dealExpiresAt, from the database) or
  // when the seller cancelled it (the deal log's cancel step). Expired and cancelled show the time too.
  const cancelledAt = dealLogs.find((row) => row.eventType === "deal_cancelled")?.creationTime || null;
  const headerMoment = status === "expired" ? detail.dealExpiresAt : status === "cancelled" ? cancelledAt : null;

  const odometerKm = Number(detail.carInfoFeedingStall);
  const hasOdometer = Number.isFinite(odometerKm) && odometerKm > 0;
  // languageCode (useLanguage's resolvedLanguage-based reading), not
  // i18n.language directly — the latter can hold the raw requested tag
  // (e.g. "en-US") while resolution/fallback leaves the app rendering sv,
  // which was silently pinning every isEnglish check on this page to sv.
  const isEnglish = languageCode === "en";
  const mileageContext = (km) => {
    const context = `${new Intl.NumberFormat(isEnglish ? "en-GB" : "sv-SE").format(km)} km`;
    return context === formatHistoricMileage(km, languageCode) ? undefined : context;
  };
  // Agreed key count, known defects and included equipment are never
  // persisted past the live signing session on this backend (seller/buyer
  // typed them into a form that only ever broadcast over SignalR) — shown
  // here as fixed placeholder copy per instruction, not read from this deal.
  // Translated via tp() (public/translations.json) like every other string
  // on this page, so English/Swedish both have their own real copy instead
  // of an inline ternary baked into the component.
  const HARDCODED_KEYS_COUNT = 2;
  const keysValue = (count) =>
    count === 1 ? tp("ds.record.contract.keys.value.one") : tp("ds.record.contract.keys.value").replace("{count}", String(count));
  const HARDCODED_DEFECTS = tp("ds.record.contract.defects.text");
  const HARDCODED_EQUIPMENT = tp("ds.record.contract.equipment.text");

  // "Signed" is backed by this app's own data when both parties' BankID
  // timestamps exist. "Payment confirmed" and "ownership transfer reported"
  // have no backing field on the contract record at all — never persisted —
  // so all three rows are shown per instruction, dated with whatever real
  // timestamp this deal has (signing, then completion, then creation), never
  // left blank.
  const signedEventDate = detail.sellerSignedAt && detail.buyerSignedAt
    ? [detail.sellerSignedAt, detail.buyerSignedAt].sort().slice(-1)[0]
    : null;
  const hasAgreement = true;

  // The receipt's paper, from the same facts the closing page uses: the vehicle, the agreed price,
  // the parties, and each event's own date from the deal's log (payment confirmed, ownership reported).
  const paymentConfirmedAt = dealLogs.find((row) => row.eventType === "payment_confirmed")?.creationTime || null;
  const receiptDocument = buildReceiptDocument({
    contractId: id,
    registrationNo: detail.carInfoRegistrationNo,
    vehicleName,
    amount: detail.carValuationBySeller,
    sellerName: formatPersonName(detail.sellerName) || "",
    buyerName: formatPersonName(detail.buyerName) || "",
    signedAt: signedEventDate,
    paidAt: paymentConfirmedAt,
    transferredAt: ownershipReportedAt,
    tp,
    language: languageCode,
  });

  return (
    <AuthShell t={t} activeKey="history">
      <section
        data-testid="transaction-record"
        className="ds-record ds-animate-page ds-auth-rail max-w-[1040px] pb-[var(--ds-tr-bottom)] pt-[var(--ds-record-top)]"
      >
        {/* Breadcrumb */}
        <div className="mb-4 flex flex-wrap items-center gap-2 text-[13px] text-ds-muted">
          <Link
            to="/affarshistorik"
            className="border-b border-[rgba(11,122,85,.45)] py-[var(--ds-crumb-y)] pr-1 text-[13px] font-medium text-ds-ink no-underline hover:text-ds-green"
          >
            {t("history.title")}
          </Link>
          <span aria-hidden="true">/</span>
          <span className="ds-mono text-[13px]">{crumbLeaf}</span>
        </div>

        {/* Header */}
        <div>
          <div className="text-[13px] font-semibold">
            <span className={status === "completed" ? "text-ds-green" : "text-ds-muted"}>
              {status === "completed" ? tp("ds.record.eyebrow.completed") : STATUS_LABELS[status]}
            </span>
            {completedDate && <span className="font-medium text-ds-muted"> · {localDate(completedDate)}</span>}
            {!completedDate && headerMoment && (
              <span data-testid="record-status-moment" className="font-medium text-ds-muted">
                {" "}
                · {localDate(headerMoment)} · {localTime(headerMoment)}
              </span>
            )}
          </div>

          <div className="mt-2.5 min-[761px]:flex min-[761px]:flex-wrap min-[761px]:items-center min-[761px]:gap-x-6 min-[761px]:gap-y-4">
            {detail.carInfoRegistrationNo && (
              <RegistrationPlate value={detail.carInfoRegistrationNo} size="object" twoRow={vehicleTypeKey === "mc"} />
            )}
            <h1 className="ds-title-route m-0 max-[760px]:mt-4">{title}</h1>
          </div>

          <div className="mt-2.5 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[15px] font-medium text-ds-text">
            {(detail.carInfoYearModel || detail.carInfoVehicleYears) && (
              <span>
                {detail.carInfoYearModel || detail.carInfoVehicleYears} · {detail.vahicleTypeOptionName || ""}
              </span>
            )}
            <span className="font-normal text-ds-muted">
              {tp("ds.record.reference")} <span className="ds-mono text-[13px] tracking-[.02em]">#{detail.id}</span>
            </span>
          </div>

          {/* Terms band */}
          <div className="mt-7 border-b border-t-2 border-b-[rgba(20,36,46,.12)] border-t-ds-ink">
            <div className="flex flex-col min-[560px]:flex-row min-[560px]:flex-wrap min-[560px]:gap-x-14 min-[560px]:gap-y-4 min-[560px]:py-[18px]">
              <div className="py-3.5 min-[560px]:py-0">
                <div className="ds-label">{tp("ds.record.agreedPrice")}</div>
                <div
                  className="ds-serif mt-1 font-semibold tabular-nums leading-[1.1] text-ds-ink"
                  style={{ fontSize: "clamp(28px, 2.4vw, 34px)" }}
                >
                  {detail.carValuationBySeller > 0 ? formatSekAmount(detail.carValuationBySeller, languageCode) : "—"}
                </div>
              </div>

              <div className="flex flex-wrap items-start gap-x-6 gap-y-3 border-[rgba(20,36,46,.12)] py-3.5 max-[559px]:border-t min-[560px]:py-0">
                <div>
                  <div className="ds-label">{tp("ds.record.party.seller")}</div>
                  <div className="mt-1 text-[17px] font-semibold leading-[1.3] text-ds-ink">
                    {detail.sellerName || "—"}
                  </div>
                </div>
                <ForwardArrow className="mt-[27px] self-start" stroke="rgba(20,36,46,.45)" width={22} height={12} />
                <div>
                  <div className="ds-label">{tp("ds.record.party.buyer")}</div>
                  <div className="mt-1 text-[17px] font-semibold leading-[1.3] text-ds-ink">
                    {detail.buyerName || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dossier — two zones of one evidence object, matching the mockup's
            own L3 record sheet: "What was agreed" beside "Documents". */}
        <section
          data-testid="record-dossier"
          className="mt-[var(--ds-tr-open-gap)] rounded-[14px] border border-ds-sheet-edge bg-ds-sheet p-6 min-[761px]:p-[30px]"
          style={{ boxShadow: "0 1px 3px rgba(16,30,40,.06)" }}
        >
          <div className={hasAgreement ? "flex flex-col min-[1024px]:flex-row" : "flex flex-col"}>
            {hasAgreement && (
              <section data-testid="record-agreement" className="min-w-0 flex-1 min-[1024px]:pr-8">
                <h2 className="ds-title-section m-0">{tp("ds.record.agreed.title")}</h2>
                <p className="m-0 mt-2 max-w-[60ch] text-[13.5px] leading-[1.55] text-ds-muted">
                  {tp("ds.record.agreed.note")}
                </p>
                <div className="mt-5 flex flex-col min-[560px]:flex-row min-[560px]:flex-wrap min-[560px]:gap-y-4">
                  {hasOdometer && (
                    <RegisterCell
                      label={tp("ds.record.contract.odometer")}
                      value={formatHistoricMileage(odometerKm, languageCode)}
                      secondary={mileageContext(odometerKm)}
                    />
                  )}
                  <RegisterCell label={tp("ds.record.contract.keys")} value={keysValue(HARDCODED_KEYS_COUNT)} />
                </div>

                <div className="mt-8">
                  <span className="ds-label">{tp("ds.record.contract.defects")}</span>
                  <p className="m-0 mt-2 max-w-[60ch] whitespace-pre-line text-[16px] leading-[1.625] text-ds-text">{HARDCODED_DEFECTS}</p>
                </div>
                <div className="mt-6">
                  <span className="ds-label">{tp("ds.record.contract.equipment")}</span>
                  <p className="m-0 mt-2 max-w-[60ch] whitespace-pre-line text-[16px] leading-[1.625] text-ds-text">{HARDCODED_EQUIPMENT}</p>
                </div>
              </section>
            )}

            {/* Documents — the real signed contract this deal produced, as
                one paper object, plus the mockup's other two retained
                transaction documents (vehicle report, receipt) shown as
                hardcoded rows per instruction: this backend never generates
                or stores those two files, so there is nothing of their own
                to open — they reuse the one real document this deal does
                have rather than linking nowhere. "View the contract" opens
                the actual contract page the buyer/seller themselves saw,
                never a raw PDF. */}
            <section
              data-testid="record-documents"
              className={
                hasAgreement
                  ? "mt-8 w-full border-t border-[rgba(20,36,46,.12)] pt-7 min-[1024px]:mt-0 min-[1024px]:w-[353px] min-[1024px]:flex-none min-[1024px]:border-l min-[1024px]:border-t-0 min-[1024px]:pl-8 min-[1024px]:pt-0"
                  : "w-full"
              }
            >
              <h2 className="ds-title-section m-0">{tp("ds.record.documents.title")}</h2>

              <div className="ds-contract-object ds-contract-object-auto mt-5">
                <button
                  type="button"
                  onClick={handleViewContract}
                  aria-label={tp("ds.record.contract.view")}
                  className="group flex w-full items-start gap-4 rounded-lg text-left transition-opacity hover:opacity-[.92] active:scale-[.998]"
                >
                  {/* A plain paper stand-in — this backend has no page-rendered
                      thumbnail of the signed PDF to show, only the real
                      contract page itself (which "View the contract" opens). */}
                  <span
                    aria-hidden="true"
                    className="flex w-[92px] flex-none flex-col gap-[5px] rounded-[3px] border border-ds-rule-edge bg-white p-2.5"
                    style={{ aspectRatio: "138 / 181", boxShadow: "0 1px 2px rgba(16,30,40,.08)" }}
                  >
                    {[80, 60, 70, 45, 65, 50].map((w, i) => (
                      <span key={i} className="block h-[3px] rounded-full bg-[rgba(20,36,46,.1)]" style={{ width: `${w}%` }} />
                    ))}
                  </span>
                  <span className="min-w-0 flex-1 pt-0.5">
                    <span className="block text-[15px] font-semibold leading-[1.3] text-ds-ink">
                      {t("dealDetails.doc.contract")}
                    </span>
                    {signedEventDate ? (
                      <span className="mt-[6px] block text-[13px] font-normal leading-snug text-ds-muted">
                        {tp("ds.record.contract.signedBoth")} · <span className="whitespace-nowrap">{localDate(signedEventDate)}</span>
                      </span>
                    ) : (
                      <span className="mt-[6px] block text-[13px] font-normal leading-snug text-ds-muted">
                        {t("dealDetails.doc.pdf")}
                      </span>
                    )}
                    <span className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold leading-[1.3] text-ds-ink transition-colors group-hover:text-ds-green">
                      <span className="ds-underline">{tp("ds.record.contract.view")}</span>
                      <span aria-hidden="true">→</span>
                    </span>
                  </span>
                </button>
              </div>

              {/* Hardcoded shelf — no backend source for either file. */}
              <div data-testid="record-document-shelf" className="mt-8 border-t border-ds-rule-row pt-1.5">
                {[
                  {
                    title: tp("ds.document.vehicleReport"),
                    meta: tp("ds.record.document.retrieved").replace("{date}", localDate(detail.creationTime)),
                  },
                  {
                    title: tp("ds.document.receipt"),
                    meta: tp("ds.record.document.created").replace("{date}", localDate(completedDate || detail.creationTime)),
                    onView: handleViewReceipt,
                    onDownload: handleViewReceipt,
                  },
                  // Real, stored BankID evidence — only once the agreement is fully signed.
                  ...(detail.dealComplete
                    ? [{ title: tp("ds.document.evidence"), meta: tp("ds.record.document.evidenceMeta"), onDownload: handleDownloadEvidence }]
                    : []),
                ].map((doc, index) => (
                  <React.Fragment key={doc.title}>
                    {index > 0 && <div aria-hidden="true" className="border-t border-ds-rule-row" />}
                    <div className="group flex items-center rounded-[10px] transition-colors hover:bg-[rgba(20,36,46,.045)]">
                      <button
                        type="button"
                        onClick={doc.onView || handleViewContract}
                        className="flex min-w-0 flex-1 items-center gap-[13px] rounded-[10px] px-2.5 py-2.5 text-left"
                      >
                        <span className="flex w-[27px] flex-none justify-center" aria-hidden="true">
                          <PdfFileMark width={20} height={23} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[15px] font-semibold leading-[1.3] text-ds-ink">{doc.title}</span>
                          <span className="mt-[2px] block text-[13px] font-normal leading-snug text-ds-muted">{doc.meta}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={doc.onDownload || handleDownloadPdf}
                        aria-label={t("actions.downloadPdf")}
                        title={t("actions.downloadPdf")}
                        className="mx-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-full text-ds-muted transition-colors hover:bg-[rgba(20,36,46,.07)] hover:text-ds-ink"
                      >
                        <DownloadMark />
                      </button>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </section>
          </div>
        </section>

        {/* Deal log — the steps of this deal as they were recorded (tbl_DealLog): the seller's car
            search, the buyer joining, the QR scan, each party confirming the price and each BankID
            signature. Shown only when the deal has recorded steps. */}
        {dealLogs.length > 0 && (
          <section data-testid="record-verification" aria-labelledby="record-events-heading" className="mt-[var(--ds-tr-close-gap)]">
            <h2 id="record-events-heading" className="ds-title-section m-0">{tp("ds.record.events.heading")}</h2>
            <ol data-testid="record-event-ledger" className="m-0 mt-2.5 list-none border-t border-[rgba(20,36,46,.25)] p-0">
              {dealLogs.map((row) => logRow(row, "record-event"))}
            </ol>
          </section>
        )}

        {/* The wrong-buyer episodes of this sale. A buyer who joined and was disconnected is not a
            step in how the transaction was completed, so this sits apart from the ledger and stays
            closed until asked for — a footnote to the deal rather than part of its account. Once
            open it is the same list, because it is the same kind of fact. */}
        {wrongBuyerLogs.length > 0 && (
          <section data-testid="record-wrong-buyer" className="mt-[var(--ds-tr-close-gap)]">
            <button
              type="button"
              onClick={() => setWrongBuyerOpen((open) => !open)}
              aria-expanded={wrongBuyerOpen}
              aria-controls="record-wrong-buyer-ledger"
              data-testid="record-wrong-buyer-toggle"
              className="ds-action-text"
            >
              <span className="ds-underline">
                {tp(wrongBuyerOpen ? "ds.record.wrongBuyer.hide" : "ds.record.wrongBuyer.view")}
              </span>
            </button>
            {wrongBuyerOpen && (
              <>
                <h2 id="record-wrong-buyer-heading" className="ds-title-section m-0 mt-5">
                  {tp("ds.record.wrongBuyer.heading")}
                </h2>
                <ol
                  id="record-wrong-buyer-ledger"
                  aria-labelledby="record-wrong-buyer-heading"
                  data-testid="record-wrong-buyer-ledger"
                  className="m-0 mt-2.5 list-none border-t border-[rgba(20,36,46,.25)] p-0"
                >
                  {wrongBuyerLogs.map((row) => logRow(row, "record-wrong-buyer-event"))}
                </ol>
              </>
            )}
          </section>
        )}

        {status === "in_progress" && (
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Button type="button" onClick={handleContinue}>
              {t("dealDetails.continue")}
            </Button>
            {isSeller && (
              <button type="button" onClick={handleCancelSale} data-testid="record-cancel-sale" className="ds-action-text">
                <span className="ds-underline">{t("seller.cancelSale")}</span>
              </button>
            )}
          </div>
        )}
      </section>

      <ContractPreviewModal t={t} open={contractPreviewOpen} onClose={() => setContractPreviewOpen(false)} id={id} viewerRole={isSeller ? "seller" : "buyer"} />
      <PaperPreviewModal
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        title={tp("ds.document.receipt")}
        document={receiptOpen ? receiptDocument : null}
        fileName={`Klargo-Receipt-${detail.carInfoRegistrationNo || id}.pdf`}
        labels={{ download: t("actions.downloadPdf"), close: t("dealDetails.action.close"), pageOf: tp("contract.preview.pageOf") }}
      />
    </AuthShell>
  );
}
